import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { CheckIcon, ClockIcon, XCircleIcon } from "@heroicons/react/24/outline";
import { CheckoutShell } from "@/modules/checkout/components/CheckoutShell/CheckoutShell";
import { Cart, SubmitInput, SubmitResult } from "@/modules/checkout/types";
import { getPublicPaymentRequest } from "../../api/getPublicPaymentRequest";
import { payPaymentRequest } from "../../api/payPaymentRequest";
import { PublicPaymentRequest } from "../../types/paymentRequest";

export function PublicPayPage() {
  const { token } = useParams<{ token: string }>();
  const [paymentRequest, setPaymentRequest] =
    useState<PublicPaymentRequest | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [paid, setPaid] = useState(false);

  useEffect(() => {
    if (!token) {
      setLoadError("Missing payment link token.");
      setLoading(false);
      return;
    }
    let cancelled = false;
    getPublicPaymentRequest(token)
      .then((res) => {
        if (cancelled) return;
        setPaymentRequest(res.paymentRequest);
      })
      .catch(() => {
        if (cancelled) return;
        setLoadError(
          "We couldn't find this payment link. It may have been cancelled or removed.",
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  if (loading) {
    return (
      <PageFrame>
        <p className="text-gray-500 dark:text-gray-400">Loading…</p>
      </PageFrame>
    );
  }

  if (loadError || !paymentRequest) {
    return (
      <PageFrame>
        <StatusPanel
          body={loadError ?? "Please contact the sender for assistance."}
          icon={<XCircleIcon className="h-10 w-10 text-gray-400" />}
          title="Payment link unavailable"
        />
      </PageFrame>
    );
  }

  if (paid || paymentRequest.status === "completed") {
    return (
      <PageFrame>
        <StatusPanel
          body="Thank you! Your payment has been processed. A receipt has been sent to your email."
          icon={<CheckIcon className="h-10 w-10 text-green-500" />}
          title="Payment received"
        />
      </PageFrame>
    );
  }

  if (paymentRequest.status === "expired") {
    return (
      <PageFrame>
        <StatusPanel
          body={`Please contact ${paymentRequest.companyName} to request a new link.`}
          icon={<ClockIcon className="h-10 w-10 text-amber-500" />}
          title="This payment link has expired"
        />
      </PageFrame>
    );
  }

  if (paymentRequest.status === "cancelled") {
    return (
      <PageFrame>
        <StatusPanel
          body={`Please contact ${paymentRequest.companyName} if you have questions.`}
          icon={<XCircleIcon className="h-10 w-10 text-gray-400" />}
          title="This payment request was cancelled"
        />
      </PageFrame>
    );
  }

  const cart: Cart = {
    lines: paymentRequest.enrollments.map((e) => ({
      sku: `enrollment-${e.enrollmentId}`,
      name: e.eventName,
      description: `${e.attendeeName}${
        e.sessionName ? ` — ${e.sessionName}` : ""
      }`,
      quantity: 1,
      unitPrice: Number(e.amount),
      metadata: {
        requiresShipping: false,
        voucherEligible: false,
        discountCodeEligible: false,
      },
    })),
    discountLines: [],
    subtotal: Number(paymentRequest.amount),
    total: Number(paymentRequest.amount),
    needsShipping: false,
    acceptsDiscountCode: false,
    acceptsVouchers: false,
    adminCanDiscount: false,
  };

  const handleSubmit = async (input: SubmitInput): Promise<SubmitResult> => {
    if (!input.payment || input.payment.kind !== "token" || !token) {
      throw new Error("Card details are required to complete payment.");
    }
    const { token: acceptToken } = input.payment;
    const result = await payPaymentRequest(token, {
      dataDescriptor: acceptToken.dataDescriptor,
      dataValue: acceptToken.dataValue,
      cardExpiration: acceptToken.cardExpiration ?? "",
      name: acceptToken.name,
      last4: acceptToken.last4,
    });
    setPaid(true);
    return {
      confirmationNumber: result.transactionId,
      successPath: "",
    };
  };

  return (
    <PageFrame>
      <div className="mb-8 text-center">
        <h1 className="text-2xl font-normal text-gray-900 dark:text-white">
          Complete your payment
        </h1>
        <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
          {paymentRequest.companyName} has requested payment for the items
          below.
        </p>
      </div>
      <CheckoutShell cart={cart} onSubmit={handleSubmit} paymentMode="inline" />
    </PageFrame>
  );
}

function PageFrame({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-slate-950 py-12 px-4">
      <div className="max-w-3xl mx-auto">{children}</div>
    </div>
  );
}

function StatusPanel({
  icon,
  title,
  body,
}: {
  icon: React.ReactNode;
  title: string;
  body: string;
}) {
  return (
    <div className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg p-12 text-center">
      <div className="flex justify-center mb-4">{icon}</div>
      <h1 className="text-xl font-normal text-gray-900 dark:text-white">
        {title}
      </h1>
      <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">{body}</p>
    </div>
  );
}
